import { useEffect, useReducer, useState } from 'react'
import { Button, Form, InputGroup } from 'react-bootstrap'
import { FaLock, FaShareAlt } from 'react-icons/fa'
import { IoIosHelpCircle } from 'react-icons/io'
import { useAccount } from '../../context/account'
import { Authorization } from '../../firebase'
import { HELP_NOTION_LINKS, logEvent } from '../../utils'
import { IconWrapper } from '../IconWrapper'
import { Required } from '../Required'
import { PremiumPlanPaymentPopup } from '../subscription/PremiumPlanPaymentPopup'
import {
  AuthorizedUidsFieldArray,
  LimitationTypeField,
  LimitedAuthorizationSetting,
} from './LimitedAuthorizationSetting'

type Props = {
  sharedPath: string
  visibilityField: {
    value: Authorization
    onChange: (...event: any[]) => void
  }
  visibleUidsFieldArray: AuthorizedUidsFieldArray
  limitationTypeField: LimitationTypeField
}

export const VisibilitySettingFormGroup = ({
  sharedPath,
  visibilityField,
  visibleUidsFieldArray,
  limitationTypeField,
}: Props) => {
  const { account } = useAccount()
  const selectableAuthorization = account?.features?.visibility_select ?? false
  const [premiumPlanPopupShow, setPremiumPlanPopupShow] = useState(false)
  const [limitedScopeModalShow, setLimitedScopeModalShow] = useState(false)
  const [isFirstSelectLimited, updateIsFirstSelectLimited] = useReducer(
    () => true,
    false
  )
  const [preSelectLimited, updatePreSelectLimited] = useReducer(
    () => false,
    true
  )

  // 初回の限定公開選択時に自動的にモーダルが開くように
  useEffect(() => {
    if (visibilityField.value !== 'limited' || isFirstSelectLimited) return
    if (preSelectLimited) return
    setLimitedScopeModalShow(true)
    updateIsFirstSelectLimited()
  }, [visibilityField.value])

  return (
    <>
      <div
        onClick={() => {
          if (!selectableAuthorization) {
            // 限定公開の機能アクセス時のイベント収集
            logEvent('tap_limited_release')
            setPremiumPlanPopupShow(true)
          }
        }}
      >
        <h2 className="h6 fw-bold">
          公開範囲
          <Required />
          <a href={HELP_NOTION_LINKS.VISIBILITY_SELECT} target="_blank">
            <IoIosHelpCircle
              className="ms-1 cursor-pointer text-black"
              onClick={() => {
                logEvent('tap_visibility_select_help')
              }}
            />
          </a>
          {!selectableAuthorization && (
            <IconWrapper
              suffix="プレミアム限定"
              className="mx-2 fs-7 text-primary cursor-pointer"
            >
              <FaLock />
            </IconWrapper>
          )}
        </h2>
        <Form.Group className="mb-4">
          <InputGroup>
            <Form.Select
              onChange={(e) => {
                if (preSelectLimited) updatePreSelectLimited()
                visibilityField.onChange(e)
              }}
              value={visibilityField.value}
              disabled={!selectableAuthorization}
            >
              <option value="public">一般公開</option>
              <option value="limited" disabled={!selectableAuthorization}>
                限定公開
              </option>
              <option value="private" disabled={!selectableAuthorization}>
                非公開
              </option>
            </Form.Select>
            {selectableAuthorization && visibilityField.value === 'limited' && (
              <Button
                onClick={() => setLimitedScopeModalShow(true)}
                className="cursor-pointer user-select-auto"
              >
                <IconWrapper suffix="共有設定">
                  <FaShareAlt />
                </IconWrapper>
              </Button>
            )}
          </InputGroup>
        </Form.Group>
      </div>
      <LimitedAuthorizationSetting
        title="限定公開の設定"
        help={HELP_NOTION_LINKS.VISIBILITY_SELECT}
        helpEvent="tap_visibility_select_help"
        show={limitedScopeModalShow}
        onHide={() => setLimitedScopeModalShow(false)}
        sharedPath={sharedPath}
        authorizedUidsFieldArray={visibleUidsFieldArray}
        limitationTypeField={limitationTypeField}
      />
      <PremiumPlanPaymentPopup
        show={premiumPlanPopupShow}
        onHide={() => setPremiumPlanPopupShow(false)}
      />
    </>
  )
}
